import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'
import { User, LoginRequest, ProductEntitlement } from '@/types'
import { apiClient, clearAuthToken } from '@/api/client'
import { clearValuationSession } from '@/pages/valuation/_valuationSession'

interface AuthContextValue {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (req: LoginRequest) => Promise<void>
  logout: () => Promise<void>
  refreshToken: () => Promise<boolean>
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

interface MeResponse {
  email: string
  name: string
  role: string
  account_role?: string
  firmId?: string
  firm?: string
  must_change_password?: boolean
  onboarding_complete?: boolean
  sso_only?: boolean
  tos_accepted?: boolean
  tos_version?: string | null
  /** PLAT-USER-NAME-1: stored name is blank or an email. */
  name_missing?: boolean
  // RBAC-1. Both are derived server-side from the same entitled_products() the
  // route gates use, so this payload cannot disagree with what /api enforces.
  // `products` lists EVERY purchasable product, un-bought ones included — the
  // nav greys those rather than hiding them, so it must not be filtered here.
  entitlements?: string[]
  products?: ProductEntitlement[]
}

export function AuthProvider({ children }: { children: ReactNode }) {
  // Synchronous E2E bootstrap — runs before first render so ProtectedRoute sees
  // isAuthenticated=true immediately and never redirects to /login.
  // useEffect (async) is too late: ProtectedRoute renders first and redirects.
  const [user, setUser] = useState<User | null>(() => {
    const testToken = localStorage.getItem('playwright_test_token')
    if (testToken && localStorage.getItem('playwright_e2e_mode') === 'true') {
      try {
        const parts = testToken.split('.')
        const claims = parts.length === 3 ? JSON.parse(atob(parts[1])) : {}
        // No `products` here on purpose: this identity is decoded from a JWT,
        // and entitlements are not in the token — they come from /v1/me, which
        // has not been called yet. Nav and landing both treat `undefined` as
        // "not known yet" and fall back to the role map rather than greying
        // everything out. The server still gates every route.
        return {
          email: claims.email || 'test@example.com',
          name: claims.name || claims.email || 'Test User',
          role: claims.role || 'super_admin',
          firm: claims.firm_id || 'yansar',
          user_id: 'test-user',
          must_change_password: false,
          onboarding_complete: true,
          sso_only: false,
          tos_accepted: true,
          tos_version: '1.0',
        } as User
      } catch { /* fall through */ }
    }
    return null
  })
  const [isLoading, setIsLoading] = useState(() =>
    !(localStorage.getItem('playwright_test_token') &&
      localStorage.getItem('playwright_e2e_mode') === 'true')
  )

  async function _attemptRefresh(): Promise<boolean> {
    try {
      // C-08: refresh token is the httpOnly qoe_refresh_token cookie — no body needed.
      await apiClient.post('/auth/refresh')
      const meRes = await apiClient.get<MeResponse>('/v1/me')
      setUser({ ...meRes.data, user_id: '', firm: meRes.data.firmId ?? meRes.data.firm ?? '', account_role: meRes.data.account_role } as User)
      return true
    } catch {
      return false
    }
  }

  useEffect(() => {
    const init = async () => {
      // E2E mode: user + isLoading already resolved synchronously in useState — skip async init.
      if (localStorage.getItem('playwright_test_token') &&
          localStorage.getItem('playwright_e2e_mode') === 'true') return

      // C-08: the httpOnly qoe_access_token cookie is the ground truth for auth
      // state — there is no token in localStorage to check anymore.
      try {
        const res = await apiClient.get<MeResponse>('/v1/me')
        setUser({ ...res.data, user_id: '', firm: res.data.firmId ?? res.data.firm ?? '', account_role: res.data.account_role } as User)
      } catch {
        const refreshed = await _attemptRefresh()
        if (!refreshed) {
          clearAuthToken()
        }
      } finally {
        setIsLoading(false)
      }
    }
    // Fix 5: bounded safety fallback — force isLoading=false after 8s if init() hangs
    // (prevents skeleton from permanently pinning during cold backend startup)
    const initPromise = init()
    const timeout = setTimeout(() => setIsLoading(false), 8000)
    Promise.resolve(initPromise).finally(() => clearTimeout(timeout))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const login = useCallback(async (req: LoginRequest) => {
    await apiClient.post('/auth/login', req)
    // Fix 1: hydrate identity from authoritative /v1/me after login succeeds
    // (not from thin login response that omits tos_accepted and returns firm_name)
    try {
      const meRes = await apiClient.get<MeResponse>('/v1/me')
      setUser({ ...meRes.data, user_id: '', firm: meRes.data.firmId ?? meRes.data.firm ?? '', account_role: meRes.data.account_role } as User)
      localStorage.setItem('qoe_user', JSON.stringify(meRes.data))
    } catch {
      // Fallback: if /v1/me fails, clear auth so user re-authenticates
      clearAuthToken()
      throw new Error('Failed to load user profile after login')
    }
  }, [])

  const logout = useCallback(async () => {
    const wasSsoOnly = user?.sso_only ?? false
    try {
      await apiClient.post('/auth/logout')
    } catch { /* best effort */ } finally {
      clearAuthToken()
      // VAL-SESSION-HYGIENE-1 R1: valuation:tuning:<id> is the body Finalize is
      // handed — leaving it in sessionStorage past sign-out lets the next
      // person who signs in on this machine open the engagement and find the
      // previous user's methodology inputs still loaded with the gate green.
      clearValuationSession()
      setUser(null)
    }
    // H1: password-login users never had a CIAM session — send them straight
    // to /login instead of round-tripping through the IdP's end_session_endpoint.
    if (!wasSsoOnly) return
    try {
      const res = await apiClient.get<{ logout_url: string | null }>('/auth/logout-url')
      if (res.data.logout_url) {
        // Full top-level navigation — federated logout requires the browser to
        // actually visit the IdP, a fetch() cannot terminate its session cookie.
        window.location.href = res.data.logout_url
      }
    } catch { /* best effort — local cookies are already cleared above */ }
  }, [user])

  const refreshToken = useCallback(async (): Promise<boolean> => {
    return _attemptRefresh()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const refreshUser = useCallback(async () => {
    const res = await apiClient.get<MeResponse>('/v1/me')
    setUser({ ...res.data, user_id: '', firm: res.data.firmId ?? res.data.firm ?? '', account_role: res.data.account_role } as User)
  }, [])

  return (
    <AuthContext.Provider value={{
      user, isAuthenticated: !!user, isLoading, login, logout, refreshToken, refreshUser
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}
